import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Button, Checkbox, Divider, Popover, Radio, Switch } from 'antd';
import type { RadioChangeEvent } from 'antd';
import SearchBox from 'components/molecules/SearchBox';
import FieldsIcon from 'assets/svg/Fields';
import FilterIcon from 'assets/svg/Filter';
import NewGlobalFilter from 'components/molecules/common/NewGlobalFilter/NewGlobalFilter';
import FilterRibbon from 'components/molecules/PricingSimulator/FilterRibbon/FilterRibbon';
import { tpoGenUrls } from 'config/urls';
import { transformFiltersV2 } from 'utils/helperMethods';
import {
    TPO_PLAN_INDEX_CREATE_PLAN_BTN,
    TPO_PLAN_INDEX_CREATE_REPORT_BTN,
    TPO_PLAN_INDEX_FIELDS_BTN,
    TPO_PLAN_INDEX_FIELDS_POPOVER,
    TPO_PLAN_INDEX_FILTER,
    TPO_PLAN_INDEX_FILTER_RIBBON,
    TPO_PLAN_INDEX_MY_PLANS_SWITCH,
    TPO_PLAN_INDEX_SEARCH_INPUT,
    TPO_PLAN_INDEX_TAB_COMPARISON,
    TPO_PLAN_INDEX_TAB_PLANS,
    TPO_PLAN_INDEX_TOOLBAR,
    tpoOptionTestId,
} from '../config/testIds';
import { useTpoGenConfig } from '../context/CustomerConfigContext';
import type { PlanIndexFilterState } from 'utils/planIndexFilterParams';
import type { PlanIndexTab } from './PlanIndex';
import { TPOGEN_PLANS_FILTER_APPLIED } from 'utils/mixpanelEventNames';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../utils/trackTpoGenEvent';

const FieldMultiSelect = ({
    fieldOptions,
    selectedFields,
    setSelectedFields,
}: {
    fieldOptions: { key: string; label: string }[];
    selectedFields: string[];
    setSelectedFields: Dispatch<SetStateAction<string[]>>;
}) => {
    const tpoGenConfig = useTpoGenConfig();
    const realFields = fieldOptions.filter((f) => f.key !== 'all');
    const allSelected = realFields.length > 0 && realFields.every((f) => selectedFields.includes(f.key));
    const someSelected = !allSelected && realFields.some((f) => selectedFields.includes(f.key));

    const handleSelectAll = () => {
        if (allSelected) {
            setSelectedFields([]);
        } else {
            setSelectedFields(realFields.map((f) => f.key));
        }
    };

    const handleFieldToggle = (field: string) => {
        if (selectedFields.includes(field)) {
            setSelectedFields((prev) => prev.filter((f) => f !== field));
        } else {
            setSelectedFields((prev) => [...prev, field]);
        }
    };

    return (
        <div className="py-3" data-testid={TPO_PLAN_INDEX_FIELDS_POPOVER}>
            <p className="text-[#807E86] text-xs">{tpoGenConfig.labels.planIndex.selectFields}</p>
            <Divider className="!my-[0.3125rem]" />
            <div className="flex flex-col items-start">
                <div className="px-3 py-[0.3125rem]">
                    <Checkbox
                        data-testid={`${TPO_PLAN_INDEX_FIELDS_POPOVER}_select_all`}
                        data-state-all-selected={allSelected ? 'true' : undefined}
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={handleSelectAll}
                    >
                        <p className="text-sm">Select All</p>
                    </Checkbox>
                </div>
                {realFields.map((_field) => (
                    <div className="px-3 py-[0.3125rem]" key={_field.key}>
                        <Checkbox
                            data-testid={tpoOptionTestId(TPO_PLAN_INDEX_FIELDS_POPOVER, _field.key)}
                            data-state-selected={selectedFields.includes(_field.key) ? 'true' : undefined}
                            checked={selectedFields.includes(_field.key)}
                            onChange={() => handleFieldToggle(_field.key)}
                        >
                            <p className="text-sm">{_field.label}</p>
                        </Checkbox>
                    </div>
                ))}
            </div>
        </div>
    );
};

interface PlanIndexToolbarProps {
    activeTab: PlanIndexTab;
    setActiveTab: (tab: PlanIndexTab) => void;
    searchKeyword: string;
    setSearchKeyword: (value: string) => void;
    showMyPlans: boolean;
    setShowMyPlans: (value: boolean) => void;
    selectedColumns: string[];
    setSelectedColumns: (value: string[]) => void;
    selectedComparisonColumns: string[];
    setSelectedComparisonColumns: (value: string[]) => void;
    allCheckedAttribute: PlanIndexFilterState['allCheckedAttribute'];
    allSelectedValueForAllAttribute: PlanIndexFilterState['allSelectedValueForAllAttribute'];
    applyFilterObject: PlanIndexFilterState['applyFilterObject'];
    setAppliedFilters: (result: PlanIndexFilterState) => void;
    resetPlanIndexFilters: () => void;
    onCreateNewPlanClick: () => void;
    onCreateNewReportClick: () => void;
}

const PlanIndexToolbar = ({
    activeTab,
    setActiveTab,
    searchKeyword,
    setSearchKeyword,
    showMyPlans,
    setShowMyPlans,
    selectedColumns,
    setSelectedColumns,
    selectedComparisonColumns,
    setSelectedComparisonColumns,
    allCheckedAttribute,
    allSelectedValueForAllAttribute,
    applyFilterObject,
    setAppliedFilters,
    resetPlanIndexFilters,
    onCreateNewPlanClick,
    onCreateNewReportClick,
}: PlanIndexToolbarProps) => {
    const tpoGenConfig = useTpoGenConfig();
    const { planIndexColumns, planIndexFilterKeys, filterDimensionInfo, scenarioComparisonConfig } = tpoGenConfig;
    const { comparisonReportColumns, comparisonReportFilterKeys } = scenarioComparisonConfig;
    const [search, setSearch] = useState<string>(() => searchKeyword);
    const [selectedFields, setSelectedFields] = useState<string[]>(() => selectedColumns);
    const [selectedComparisonFields, setSelectedComparisonFields] = useState<string[]>(() => selectedComparisonColumns);
    const selectColumnFields = [
        { key: 'all', label: 'Select All' },
        ...planIndexColumns.map((c) => ({ key: c.key, label: c.label })),
    ];
    const comparisonColumnFields = [
        { key: 'all', label: 'Select All' },
        ...comparisonReportColumns.map((c) => ({ key: c.key, label: c.label })),
    ];

    const [selectedFilter, setSelectedFilter] = useState<any>(null);
    const [tempApplyFilterObject, setTempApplyFilterObject] = useState<any>({});

    const onFilterSaveClickHandler = useCallback(
        (resultObj: PlanIndexFilterState) => {
            const filterFieldsSummary = Object.fromEntries(
                Object.entries(resultObj.applyFilterObject ?? {}).map(([field, value]) => [
                    field,
                    {
                        inclusion: value.inclusion ? Array.from(value.inclusion) : undefined,
                        exclusion: value.exclusion ? Array.from(value.exclusion) : undefined,
                    },
                ]),
            );
            trackTpoGenEvent(TPOGEN_PLANS_FILTER_APPLIED, TPOGEN_PAGE_NAMES.PLANS_LIST, {
                filter_fields: JSON.stringify(filterFieldsSummary),
            });
            setAppliedFilters(resultObj);
        },
        [setAppliedFilters],
    );

    const isPlansTab = activeTab === 'plans';

    const activeFilterKeys = isPlansTab ? planIndexFilterKeys : comparisonReportFilterKeys;

    const filterApiVariables = useMemo(() => {
        if (selectedFilter?.value) {
            const updatedFilters = transformFiltersV2(tempApplyFilterObject, activeFilterKeys, selectedFilter.value);
            return {
                attribute_type: selectedFilter.value,
                limit: 50,
                filters: updatedFilters,
            };
        }
    }, [selectedFilter, tempApplyFilterObject, activeFilterKeys]);

    const handleTabChange = useCallback(
        (e: RadioChangeEvent) => {
            setActiveTab(e.target.value as PlanIndexTab);
        },
        [setActiveTab],
    );

    return (
        <>
            <div className="flex flex-row justify-between flex-wrap" data-testid={TPO_PLAN_INDEX_TOOLBAR}>
                <div className="flex items-center">
                    <div className="flex flex-col gap-y-[0.5rem]">
                        <div className="flex gap-x-[0.75rem]">
                            <p className="text-[#18181B] font-semibold text-base">
                                {tpoGenConfig.labels.planIndex.sectionTitle}
                            </p>
                            <Radio.Group value={activeTab} onChange={handleTabChange}>
                                <Radio.Button value="plans" data-testid={TPO_PLAN_INDEX_TAB_PLANS}>
                                    {tpoGenConfig.labels.planIndex.tabs.plans}
                                </Radio.Button>
                                <Radio.Button
                                    value="comparison-reports"
                                    data-testid={TPO_PLAN_INDEX_TAB_COMPARISON}
                                >
                                    {tpoGenConfig.labels.planIndex.tabs.comparisonReports}
                                </Radio.Button>
                            </Radio.Group>
                        </div>
                        <div>
                            <p className="text-[#A49FA9] text-sm font-normal">
                                {tpoGenConfig.labels.planIndex.description}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="flex gap-x-[0.6rem] items-center">
                    <SearchBox
                        onSearch={(val: string) => setSearchKeyword(val)}
                        search={search}
                        setSearch={setSearch}
                        data-testid={TPO_PLAN_INDEX_SEARCH_INPUT}
                    />
                    <div className="flex gap-[0.75rem] items-center">
                        <p className="text-[#807E86] text-xs">
                            {isPlansTab
                                ? tpoGenConfig.labels.planIndex.myPlan
                                : tpoGenConfig.labels.comparisonReport.myReport}
                        </p>
                        <Switch
                            data-testid={TPO_PLAN_INDEX_MY_PLANS_SWITCH}
                            data-state-active={showMyPlans ? 'true' : undefined}
                            checked={showMyPlans}
                            onChange={(val: boolean) => setShowMyPlans(val)}
                        />
                    </div>
                    <NewGlobalFilter
                        availableFilters={activeFilterKeys}
                        allCheckedAttribute={allCheckedAttribute}
                        allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                        applyFilterObject={applyFilterObject}
                        resetFilterObject={resetPlanIndexFilters}
                        onSubmitHandler={onFilterSaveClickHandler}
                        url={tpoGenUrls.getPlanIndexFilterAttributes}
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                        tempApplyFilterObject={tempApplyFilterObject}
                        setTempApplyFilterObject={setTempApplyFilterObject}
                        apiVariables={filterApiVariables}
                        filterDimensionInfo={filterDimensionInfo}
                        showAttributePanelToLeft
                        filterIcon={<FilterIcon />}
                        data-testid={TPO_PLAN_INDEX_FILTER}
                    />
                    <Popover
                        trigger={'click'}
                        showArrow={false}
                        placement="bottomRight"
                        content={
                            isPlansTab ? (
                                <FieldMultiSelect
                                    fieldOptions={selectColumnFields}
                                    selectedFields={selectedFields}
                                    setSelectedFields={setSelectedFields}
                                />
                            ) : (
                                <FieldMultiSelect
                                    fieldOptions={comparisonColumnFields}
                                    selectedFields={selectedComparisonFields}
                                    setSelectedFields={setSelectedComparisonFields}
                                />
                            )
                        }
                        align={{ offset: [0, -8] }}
                        onOpenChange={(open) => {
                            if (!open) {
                                if (isPlansTab) {
                                    setSelectedColumns(
                                        selectColumnFields
                                            .filter((col) => col.key !== 'all' && selectedFields.includes(col.key))
                                            .map((col) => col.key),
                                    );
                                } else {
                                    setSelectedComparisonColumns(
                                        comparisonColumnFields
                                            .filter((col) => col.key !== 'all' && selectedComparisonFields.includes(col.key))
                                            .map((col) => col.key),
                                    );
                                }
                            }
                        }}
                    >
                        <Button data-testid={TPO_PLAN_INDEX_FIELDS_BTN}>
                            <div className="flex flex-row gap-x-[0.5rem] items-center">
                                <FieldsIcon />
                                <p>
                                    {isPlansTab
                                        ? tpoGenConfig.labels.planIndex.fields
                                        : tpoGenConfig.labels.comparisonReport.columns}
                                </p>
                            </div>
                        </Button>
                    </Popover>
                    {isPlansTab ? (
                        <Button
                            type="primary"
                            data-testid={TPO_PLAN_INDEX_CREATE_PLAN_BTN}
                            onClick={onCreateNewPlanClick}
                        >
                            {tpoGenConfig.labels.planIndex.createNewPlan}
                        </Button>
                    ) : (
                        <Button
                            type="primary"
                            data-testid={TPO_PLAN_INDEX_CREATE_REPORT_BTN}
                            onClick={onCreateNewReportClick}
                        >
                            {tpoGenConfig.labels.comparisonReport.createNewReport}
                        </Button>
                    )}
                </div>
                <Divider className="!my-3" />
            </div>

            <div className="mb-3">
                <FilterRibbon
                    allCheckedAttribute={allCheckedAttribute}
                    allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                    applyFilterObject={applyFilterObject}
                    resetFilterObject={resetPlanIndexFilters}
                    onSubmitHandler={onFilterSaveClickHandler}
                    data-testid={TPO_PLAN_INDEX_FILTER_RIBBON}
                />
            </div>
        </>
    );
};

export default PlanIndexToolbar;
